import type { SelectionEntry } from 'Models/index';
import type { PlatformRequestConfig } from 'Models/uiConfig/requestConfig';
import type { RequestExtra, SearchRequestDefaults } from 'Models/uiConfig/requestTypes';
import { isTruthy } from 'Utils/guards';

import {
  getCartUrls,
  getCollectionId,
  getCompanyId,
  getCompanyLocationId,
  getCountry,
  getCurrency,
  getCurrencyRate,
  getCustomerId,
  getCustomerTags,
  getLocale,
  getPageId,
  getPageType,
  getRootRoute,
} from './platformHelpers';

const currentPageType = getPageType();
const currentCollectionId = getCollectionId();
const currentPageId = getPageId();
const rootRoute = getRootRoute();
const { add } = getCartUrls();

const referrerPosition = rootRoute ? 4 : 3;
const isReferrerInUrl =
  currentPageType === 'collection' &&
  window.location.pathname.split('/').filter(isTruthy).length >= referrerPosition;

const urlQueryParam = new URLSearchParams(window.location.search).get('q');

const pageTypeToPreselectionMap: Record<string, SelectionEntry[]> = {
  category: currentCollectionId ? [{ field: 'collection_ids', term: `${currentCollectionId}` }] : [],
  brand: urlQueryParam ? [{ field: 'vendor', term: urlQueryParam }] : [],
};

function getLocalPreselection(pageType: string): SelectionEntry[] | null {
  return pageTypeToPreselectionMap[pageType] || null;
}

function collectExtra(): RequestExtra {
  const extra: RequestExtra = {};

  if (rootRoute) {
    extra.platformRoute = rootRoute;
  }

  if (currentPageType === 'page') {
    extra.pageId = currentPageId;
  }

  const country = getCountry();
  if (country) {
    extra.country = country.toLowerCase();
  }

  const currency = getCurrency();
  if (currency) {
    extra.currency = currency.toLowerCase();
  }

  const currencyRate = getCurrencyRate();
  if (currencyRate) {
    extra.currencyRate = currencyRate;
  }

  const locale = getLocale();
  if (locale) {
    extra.locale = locale;
  }

  const companyId = getCompanyId();
  if (companyId) {
    extra.customerCompanyId = companyId;
  }

  const companyLocationId = getCompanyLocationId();
  if (companyLocationId) {
    extra.customerCompanyLocationId = companyLocationId;
  }

  const customerTags = getCustomerTags();
  if (customerTags?.length) {
    extra.customerTags = customerTags;
  }

  return extra;
}

function getSearchRequestDefaults(_pageType: string): SearchRequestDefaults {
  const requestDefaults: SearchRequestDefaults = {};

  if (isReferrerInUrl) {
    requestDefaults.referrer = encodeURIComponent(window.location.pathname);
  }

  requestDefaults.extra = collectExtra();

  return requestDefaults;
}

function getAutocompleteRequestDefaults(): RequestExtra {
  return collectExtra();
}

async function ajaxAddToCart(id: string | number, quantity = 1): Promise<Response> {
  const formData = { items: [{ id, quantity }] };

  return fetch(`${add}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData),
  });
}

async function addItemsToCart(ids: Array<string | number>): Promise<void> {
  for (const id of ids) {
    await ajaxAddToCart(id);
  }
}

Object.assign(getLocalPreselection, { defaults: { getter: getLocalPreselection } });

async function getCustomerAsync(): Promise<{ id: string | number } | null> {
  const id = getCustomerId();
  window.Convermax.customerId = id;
  window.Convermax.isLoggedIn = !!id;
  return id ? { id } : null;
}

const requestConfig: PlatformRequestConfig = {
  getLocalPreselection,
  getSearchRequestDefaults,
  addItemsToCart,
  getAutocompleteRequestDefaults,
  getCustomerAsync,
  isReferrerInUrl,
  platformRoute: rootRoute,
};

export default requestConfig;
